import dayjs from 'dayjs';
import * as React from 'react';
import { useMemo } from 'react';

import DateSelector from '@/components/elements/DateSelector';
import GradeIcon from '@/components/elements/Icon/Grade';
import ScheduleContentSelector, {
  ScheduleContentType,
} from '@/components/layouts/Schedule/ContentSelector';
import { dayjst } from '@/lib/dayjst';
import { GradeRace } from '@/lib/microcms/model';
import { toHalfWidth } from '@/utility/utils';

import { Headline } from '../../style';
import { SelectorWrapper } from '../style';
import {
  DateSelectorWrapper,
  EmptyMessage,
  HeldDate,
  Month,
  MonthList,
  MonthWrapper,
  ProgramName,
  RaceItem,
  RaceList,
  VelodromeName,
} from './style';

interface GradeRaceTemplateProps {
  date: dayjs.Dayjs;
  gradeRaces: GradeRace[];
}

const GradeRaceTemplate: React.FC<GradeRaceTemplateProps> = ({
  date,
  gradeRaces,
}) => {
  const gradeRaceList = useMemo(() => {
    const obj: { [month: number]: GradeRace[] } = {};
    gradeRaces.forEach(gr => {
      const month = dayjs(gr.heldFirstDate).month();
      if (!(month in obj)) {
        obj[month] = [];
      }
      obj[month].push(gr);
    });
    return obj;
  }, [gradeRaces]);

  // 一件でもグレードレースが存在するか
  const isRegistered = Object.values(gradeRaceList).flat().length > 0;

  const sortedByHeldDate = (grs: GradeRace[]) =>
    grs.sort((a, b) => dayjst(a.heldFirstDate).diff(dayjst(b.heldFirstDate)));

  return (
    <>
      <Headline>開催スケジュール</Headline>
      <SelectorWrapper>
        <ScheduleContentSelector
          scheduleContentType={ScheduleContentType.GradeRace}
        />
      </SelectorWrapper>
      <DateSelectorWrapper>
        <DateSelector
          date={date}
          displayFormat="YYYY年"
          pathFormat="YYYY"
          unit="year"
          clickOpens={false}
        />
      </DateSelectorWrapper>
      <MonthList>
        {isRegistered ? (
          <>
            {[...Array(12)].map((_, i) => (
              <MonthWrapper key={`graderace-list-${date.format('YYYY')}-${i}`}>
                <Month>{i + 1}月</Month>
                {gradeRaceList[i] && gradeRaceList[i].length > 0 ? (
                  <RaceList>
                    {sortedByHeldDate(gradeRaceList[i]).map(gr => (
                      <RaceItem key={`graderace-item-${gr.id}`}>
                        <HeldDate>
                          <span>{dayjst(gr.heldFirstDate).format('M/D')}</span>
                          {gr.heldLastDate && (
                            <span>
                              ~{dayjst(gr.heldLastDate).format('M/D')}
                            </span>
                          )}
                        </HeldDate>
                        <ProgramName>
                          <GradeIcon
                            gradeName={gr.grade[0]}
                            width="24px"
                            height="16px"
                          />
                          <span>{toHalfWidth(gr.programName || '')}</span>
                        </ProgramName>
                        <VelodromeName>
                          {gr.velodrome.velodromeName}
                          競輪
                        </VelodromeName>
                      </RaceItem>
                    ))}
                  </RaceList>
                ) : (
                  <EmptyMessage>開催情報がありません</EmptyMessage>
                )}
              </MonthWrapper>
            ))}
          </>
        ) : (
          <EmptyMessage>開催情報がありません</EmptyMessage>
        )}
      </MonthList>
    </>
  );
};

export default GradeRaceTemplate;
